/**
 * Outils d'une liste : recherche (envoyée après une courte pause, jamais à
 * chaque frappe), filtres dans une feuille au téléphone, nombre de résultats.
 * L'état vit dans l'adresse sous des noms communs à toute l'application :
 * q, etat, tri, vue, onglet, periode.
 */
import { useCallback, useEffect, useMemo, useState, type KeyboardEvent, type ReactNode, type Ref } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, SlidersHorizontal, X } from 'lucide-react';
import { Overlay } from '../overlay.js';
import { t } from '../../core/i18n.js';

export type ListParam =
  | 'q' | 'etat' | 'tri' | 'vue' | 'onglet' | 'periode'
  // Filtres et repères communs (docs/agents/UI.md §4) : le planning les lit ici.
  | 'date' | 'technicien' | 'type' | 'secteur' | 'client'
  // Journal d'audit : la famille d'action filtrée.
  | 'action';

/**
 * Valeurs de liste lues et écrites dans l'adresse. Une valeur par défaut
 * n'apparaît pas dans l'adresse ; changer un filtre remplace l'entrée
 * d'historique au lieu d'en empiler une par frappe. `{ push: true }` empile
 * une entrée quand le changement est une navigation que « Précédent » doit
 * défaire (la date ou la vue du planning).
 */
export function useListState<K extends ListParam>(defaults: Record<K, string>): [
  Record<K, string>,
  (patch: Partial<Record<K, string>>, options?: { push?: boolean }) => void,
] {
  const [params, setParams] = useSearchParams();
  const keys = Object.keys(defaults) as K[];
  const values = useMemo(() => {
    const current = {} as Record<K, string>;
    for (const key of keys) current[key] = params.get(key) ?? defaults[key];
    return current;
    // `defaults` est un littéral : ses clés et valeurs suffisent comme dépendance.
  }, [params, JSON.stringify(defaults)]);
  const update = useCallback(
    (patch: Partial<Record<K, string>>, options?: { push?: boolean }) => {
      setParams(
        (current) => {
          const next = new URLSearchParams(current);
          for (const [key, value] of Object.entries(patch) as [K, string | undefined][]) {
            if (value === undefined || value === '' || value === defaults[key]) next.delete(key);
            else next.set(key, value);
          }
          return next;
        },
        { replace: !options?.push },
      );
    },
    [setParams, JSON.stringify(defaults)],
  );
  return [values, update];
}

/** Valeur stabilisée après `delay` ms sans changement (recherche). */
export function useDebouncedValue<T>(value: T, delay = 250): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

/**
 * Champ de recherche arrondi, avec sa loupe et son bouton d'effacement : celui
 * des listes et de la fenêtre des paramètres. Le libellé est lu, pas affiché.
 */
export function SearchField(props: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  label?: string | undefined;
  onKeyDown?: ((event: KeyboardEvent<HTMLInputElement>) => void) | undefined;
  inputRef?: Ref<HTMLInputElement> | undefined;
}): ReactNode {
  return (
    <label className="ui-search-field">
      <Search size={18} aria-hidden="true" />
      <span className="sr-only">{props.label ?? props.placeholder}</span>
      <input
        ref={props.inputRef}
        type="search"
        value={props.value}
        placeholder={props.placeholder}
        onChange={(event) => props.onChange(event.target.value)}
        onKeyDown={props.onKeyDown}
      />
      {props.value ? (
        <button type="button" className="ui-search-field__clear" aria-label="Effacer la recherche" onClick={() => props.onChange('')}>
          <X size={16} aria-hidden="true" />
        </button>
      ) : null}
    </label>
  );
}

export function ListToolbar(props: {
  search?: { value: string; onChange: (value: string) => void; placeholder: string; label?: string };
  /** Filtres, rendus dans une feuille au téléphone et en ligne sur ordinateur. */
  filters?: ReactNode;
  /** Nombre de filtres actifs (affiché sur le bouton « Filtrer »). */
  activeFilters?: number;
  onResetFilters?: () => void;
  /** Résumé du résultat, par exemple « 42 clients ». */
  count?: ReactNode;
}): ReactNode {
  const [draft, setDraft] = useState(props.search?.value ?? '');
  const debounced = useDebouncedValue(draft);
  const onSearch = props.search?.onChange;
  useEffect(() => {
    if (onSearch && debounced !== props.search?.value) onSearch(debounced);
  }, [debounced]);
  useEffect(() => {
    // Valeur changée de l'extérieur (lien, retour arrière) : le champ suit.
    if (props.search && props.search.value !== debounced) setDraft(props.search.value);
  }, [props.search?.value]);
  return (
    <div className="ui-list-toolbar" role="search">
      {props.search ? (
        <SearchField
          value={draft}
          onChange={setDraft}
          placeholder={props.search.placeholder}
          label={props.search.label}
        />
      ) : null}
      {props.filters ? (
        <ListFilters
          filters={props.filters}
          activeFilters={props.activeFilters}
          onResetFilters={props.onResetFilters}
        />
      ) : null}
      {props.count ? <p className="ui-list-toolbar__count" role="status">{props.count}</p> : null}
    </div>
  );
}

/**
 * Les filtres seuls, pour une barre qui n'a pas de recherche (le planning) :
 * « Filtrer (n) » ouvre une feuille au téléphone, les filtres sont en ligne sur
 * ordinateur. `compact` réduit le bouton à son icône et à son compteur sur
 * téléphone, pour tenir sur une seule rangée ; son nom reste lu en entier.
 * `inline={false}` garde la feuille à toutes les largeurs, quand la rangée de
 * l'ordinateur est déjà pleine.
 */
export function ListFilters(props: {
  filters: ReactNode;
  activeFilters?: number | undefined;
  onResetFilters?: (() => void) | undefined;
  compact?: boolean;
  inline?: boolean;
}): ReactNode {
  const [sheetOpen, setSheetOpen] = useState(false);
  const inline = props.inline ?? true;
  const label = `Filtrer${props.activeFilters ? ` (${props.activeFilters})` : ''}`;
  // Nom de la variante compacte : il reprend le nombre visible sur la pastille (WCAG 2.5.3).
  const count = props.activeFilters ?? 0;
  const compactName = count ? `Filtrer, ${count} filtre${count > 1 ? 's' : ''} actif${count > 1 ? 's' : ''}` : 'Filtrer';
  return (
    <>
      <button
        type="button"
        className={[
          'btn btn--secondary ui-list-toolbar__filter-button',
          props.compact && 'ui-list-toolbar__filter-button--compact',
          inline || 'ui-list-toolbar__filter-button--sheet',
        ].filter(Boolean).join(' ')}
        aria-haspopup="dialog"
        aria-label={props.compact ? compactName : undefined}
        onClick={() => setSheetOpen(true)}
      >
        <SlidersHorizontal size={18} aria-hidden="true" />
        <span className="ui-list-toolbar__filter-label">{label}</span>
        {props.compact && props.activeFilters ? (
          <span className="ui-list-toolbar__filter-count" aria-hidden="true">{props.activeFilters}</span>
        ) : null}
      </button>
      {inline ? <div className="ui-list-toolbar__filters">{props.filters}</div> : null}
      {sheetOpen ? (
        <Overlay label="Filtres" variant="feuille" onClose={() => setSheetOpen(false)}>
          <div className="ui-filter-sheet">
            <h2>Filtres</h2>
            {props.filters}
            <div className="ui-filter-sheet__actions">
              {props.onResetFilters ? (
                <button type="button" className="btn btn--ghost" onClick={props.onResetFilters}>
                  Tout effacer
                </button>
              ) : null}
              <button type="button" className="btn" onClick={() => setSheetOpen(false)}>
                Voir les résultats
              </button>
            </div>
          </div>
        </Overlay>
      ) : null}
    </>
  );
}

/** « Charger plus » en bas d'une liste paginée. */
export function LoadMore(props: {
  hasMore: boolean;
  loading: boolean;
  /** La page suivante n'a pas pu être lue : la liste reste, le bouton le dit (WEB2-01). */
  failed?: boolean;
  onLoadMore: () => void;
}): ReactNode {
  if (!props.hasMore) return null;
  return (
    <div className="ui-load-more">
      <button type="button" className="btn btn--secondary" disabled={props.loading} onClick={props.onLoadMore}>
        {props.loading ? 'Chargement…' : props.failed ? t('commun.echecReessayer') : 'Afficher plus'}
      </button>
    </div>
  );
}
